/* Present the existing Kaktus cart in the storefront's design system. Cart data and actions remain app-owned. */
(() => {
  const source = document.currentScript;
  const stylesheet = source?.dataset.stylesheet;
  const brandIcon = source?.dataset.brandIcon;
  if (!stylesheet) return;

  const enhancedFrames = new WeakSet();
  const variantCache = new Map();
  const recommendationCache = new Map();
  const relatedProducts = (productId, currency) => {
    const root = window.Shopify?.routes?.root || '/';
    const key = `${root}:${productId}:${currency}:${Math.floor(Date.now() / 60000)}`;
    if (recommendationCache.size > 30) recommendationCache.clear();
    if (!recommendationCache.has(key)) {
      recommendationCache.set(key, fetch(`${root}recommendations/products.json?product_id=${productId}&limit=10&intent=complementary`, {
        credentials: 'same-origin', headers: { Accept: 'application/json' }
      }).then(response => response.ok ? response.json() : Promise.reject(new Error('Recommendations unavailable')))
        .catch(() => ({ products: [] }))
        .then(async data => {
          const products = Array.isArray(data.products) ? data.products : [];
          // A factual everyday extra, not an implied compatibility guarantee.
          const handles = [
            'wet-stuff-plus-270g-disk-water-based-lubricant-270-grams-bottle',
            'wet-stuff-plain-100g-tube-water-based-lubricant-with-vitamin-e-100-gram-tube',
            'wet-stuff-plain-270g-disk-water-based-lubricant-with-vitamin-e-270-gram-bottle',
            'wet-stuff-plain-550g-disktop-water-based-lubricant-with-vitamin-e-550-gram-bottle'
          ];
          const extras = await Promise.all(handles.map(handle => fetch(`${root}products/${handle}.js`, { credentials: 'same-origin', cache: 'no-store' })
            .then(response => response.ok ? response.json() : null).catch(() => null)));
          return [...products, ...extras.filter(Boolean)];
        })
        .catch(() => []));
    }
    return recommendationCache.get(key);
  };
  // This existing zero-discount offer contains stale price and availability data.
  // Read the public variant once per currency; Shopify and the app still own checkout.
  const currentVariant = (id, currency) => {
    const key = `${id}:${currency}`;
    if (!variantCache.has(key)) {
      const entry = { status: 'pending', variant: null, promise: null };
      const root = window.Shopify?.routes?.root || '/';
      entry.promise = fetch(`${root}variants/${id}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((response) => response.ok ? response.json() : Promise.reject(new Error('Variant unavailable')))
        .then((variant) => {
          if (String(variant.id) !== id || !Number.isInteger(variant.price) || typeof variant.available !== 'boolean') throw new Error('Invalid variant');
          entry.variant = variant;
          entry.status = 'ready';
        })
        .catch(() => { entry.status = 'failed'; });
      variantCache.set(key, entry);
    }
    return variantCache.get(key);
  };
  const attachFrame = (frame) => {
    if (enhancedFrames.has(frame)) return;
    enhancedFrames.add(frame);
    frame.title = 'Shopping bag';

    const enhance = () => {
      let doc;
      try { doc = frame.contentDocument; } catch (_) { return; }
      if (!doc?.head || doc.documentElement.dataset.dutCartEnhanced) return;
      doc.documentElement.dataset.dutCartEnhanced = 'true';
      doc.documentElement.classList.add('dut-cart-panel');
      // Keep the countdown hidden while the app's cart contents are still unknown.
      // This inline rule also applies before the cart stylesheet has downloaded.
      const timerGuard = doc.createElement('style');
      timerGuard.id = 'dut-cart-timer-guard';
      timerGuard.textContent = 'html:not([data-dut-cart-has-items="true"]) .kaktusc-cart__timer-wrap{display:none!important}';
      doc.head.append(timerGuard);
      const inheritTheme = () => {
        doc.documentElement.dataset.dutTheme = document.documentElement.dataset.dutTheme || 'light';
        const themeStyle = getComputedStyle(document.body);
        ['--font-body-family', '--dut-font-display'].forEach((name) => {
          const value = themeStyle.getPropertyValue(name).trim();
          if (value) doc.documentElement.style.setProperty(name, value);
        });
      };
      inheritTheme();
      new MutationObserver(inheritTheme).observe(document.documentElement, { attributes: true, attributeFilter: ['data-dut-theme'] });
      const link = doc.createElement('link');
      link.id = 'dut-cart-panel-style';
      link.rel = 'stylesheet';
      link.href = stylesheet;
      doc.head.append(link);

      const host = frame.closest('#kaktusc-app');
      let wasOpen = false;
      let returnFocus = null;
      let focusPending = false;
      let focusedControl = null;
      let requestedOpener = null;
      let readyAnnounced = false;
      const pendingOffers = new WeakMap();
      // Synchronise the header independently of recommendations, including an empty cart.
      let cartSignature = null;
      let cartSyncTimer;
      let cartSyncVersion = 0;
      const syncCartCount = (cartPanel) => {
        const lines = [...cartPanel.querySelectorAll('.kaktusc-cart__products .kaktusc-cart__product')];
        const signature = JSON.stringify(lines.map(line => [line.dataset.id, line.querySelector('.kaktusc-cart__product-qnt')?.textContent.trim()]));
        if (signature === cartSignature) return;
        cartSignature = signature;
        const version = ++cartSyncVersion;
        clearTimeout(cartSyncTimer);
        cartSyncTimer = setTimeout(async function readCart(attempt = 0) {
          if (version !== cartSyncVersion) return;
          try {
            const root = window.Shopify?.routes?.root || '/';
            const response = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store' });
            if (!response.ok) throw new Error('Cart unavailable');
            const current = await response.json();
            if (version !== cartSyncVersion || !Number.isInteger(current.item_count)) return;
            document.querySelectorAll('#cart-icon-bubble .cart-count-bubble').forEach(bubble => {
              const count = bubble.querySelector('[aria-hidden="true"]');
              const accessible = bubble.querySelector('.visually-hidden');
              if (count) count.textContent = String(current.item_count);
              if (accessible) accessible.textContent = `${current.item_count} ${current.item_count === 1 ? 'item' : 'items'}`;
            });
            window.dispatchEvent(new CustomEvent('dut:cart-data', { detail: current }));
            const expectedCount = lines.reduce((sum, line) => sum + (Number(line.querySelector('.kaktusc-cart__product-qnt')?.textContent.trim()) || 0), 0);
            if (current.item_count !== expectedCount && attempt < 3) cartSyncTimer = setTimeout(readCart, 300, attempt + 1);
          } catch (_) {
            // A later app render can retry; never replace the real count with a guessed zero.
            if (version === cartSyncVersion) cartSignature = null;
          }
        }, 120);
      };

      let recommendationKey = null;
      let recommendationData = null;
      let recommendationVersion = 0;
      const panel = () => doc.querySelector('.kaktusc-cart__body');
      const visible = (element) => element && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden';
      const isOpen = () => Boolean(host && visible(host) && visible(panel()));
      const close = () => doc.querySelector('.kaktusc-cart__close')?.click();
      // The installed app binds this generated icon to its own openCart path,
      // which reads the current Shopify cart. No private app instance or second
      // add request is needed, and a missing app leaves the native UI available.
      window.DUTCartPanel = {
        open(opener) {
          const trigger = document.getElementById('kaktusc-cart-icon');
          if (!frame.isConnected || !panel() || !trigger) return false;
          if (!isOpen()) {
            requestedOpener = opener?.isConnected ? opener : document.activeElement;
            trigger.click();
          }
          const opened = isOpen();
          if (!opened) requestedOpener = null;
          return opened;
        }
      };
      const focusables = () => [...(panel()?.querySelectorAll('a[href], button, input, select, textarea, [tabindex="0"]') || [])]
        .filter((element) => !element.disabled && visible(element));
      const button = (element, label) => {
        if (!element) return;
        element.setAttribute('aria-label', label);
        if (!['BUTTON', 'A'].includes(element.tagName)) {
          element.setAttribute('role', 'button');
          element.tabIndex = 0;
          element.dataset.dutButton = 'true';
        }
      };

      const renderRecommendations = (cart, data) => {
        let section = cart.querySelector('.dut-cart-recommendations');
        const summary = cart.querySelector('.kaktusc-cart__summary');
        if (!data.products.length || !summary) { if (section) section.hidden = true; return; }
        if (!section) {
          section = doc.createElement('section');
          section.className = 'dut-cart-recommendations';
          section.setAttribute('aria-label', 'You might also like');
          summary.before(section);
        }
        const signature = `${data.remaining || 0}:${data.currency}:${data.products.map(product => `${product.id}:${product.price}`).join('|')}`;
        if (section.dataset.signature !== signature) {
          const heading = doc.createElement('h3');
          heading.textContent = 'You might also like';
          const list = doc.createElement('div');
          list.className = 'dut-cart-recommendations__list';
          const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', { style: 'currency', currency: data.currency, currencyDisplay: 'narrowSymbol' });
          data.products.forEach(product => {
            const link = doc.createElement('a');
            link.className = 'dut-cart-recommendation';
            const productRoot = `${window.Shopify?.routes?.root || '/'}products/`;
            link.href = `${productRoot}${encodeURIComponent(product.handle)}`;
            // Keep Shopify's recommendation attribution when it points back to this storefront.
            try {
              const recommendedUrl = new URL(product.url, location.origin);
              if (recommendedUrl.origin === location.origin && recommendedUrl.pathname.startsWith(productRoot)) {
                link.href = `${recommendedUrl.pathname}${recommendedUrl.search}${recommendedUrl.hash}`;
              }
            } catch (_) {}
            if (product.dutVariantId) { const variantUrl = new URL(link.href, location.origin); variantUrl.searchParams.set('variant', product.dutVariantId); link.href = variantUrl.href; }
            link.target = '_top';
            link.setAttribute('aria-label', `View ${product.title}`);
            const image = doc.createElement('img');
            const imageUrl = new URL(product.image, location.origin);
            imageUrl.searchParams.set('width', '180');
            image.src = imageUrl.href;
            image.alt = '';
            image.width = 72;
            image.height = 84;
            image.loading = 'lazy';
            const copy = doc.createElement('span');
            copy.className = 'dut-cart-recommendation__copy';
            const title = doc.createElement('strong');
            const shortNames = {
              'wet-stuff-plus-270g-disk-water-based-lubricant-270-grams-bottle': 'Wet Stuff Plus · 270g',
              'wet-stuff-plain-100g-tube-water-based-lubricant-with-vitamin-e-100-gram-tube': 'Wet Stuff Plain · 100g',
              'wet-stuff-plain-270g-disk-water-based-lubricant-with-vitamin-e-270-gram-bottle': 'Wet Stuff Plain · 270g',
              'wet-stuff-plain-550g-disktop-water-based-lubricant-with-vitamin-e-550-gram-bottle': 'Wet Stuff Plain · 550g'
            };
            title.textContent = shortNames[product.handle] || product.title;
            title.title = product.title;
            const price = doc.createElement('span');
            price.className = 'dut-cart-recommendation__price';
            price.textContent = `${product.price_varies ? 'From ' : ''}${formatter.format(product.price / 100)}`;
            const action = doc.createElement('span');
            action.className = 'dut-cart-recommendation__action';
            action.textContent = 'View product';
            const arrow = doc.createElement('span');
            arrow.setAttribute('aria-hidden', 'true');
            arrow.textContent = ' →';
            action.append(arrow);
            copy.append(title, price, action);
            link.append(image, copy);
            list.append(link);
          });
          const note = doc.createElement('p');
          note.textContent = data.remaining
            ? `Spend ${formatter.format(data.remaining / 100)} more for free Standard shipping (AU).`
            : '';
          section.replaceChildren(heading, ...(note.textContent ? [note] : []), list);
          section.dataset.signature = signature;
        }
        section.hidden = false;
      };

      const updateRecommendations = (cart, hasItems) => {
        const section = cart.querySelector('.dut-cart-recommendations');
        // Complementary suggestions remain separate from app promotions.
        if (!hasItems) {
          if (recommendationKey !== null) { recommendationKey = null; recommendationData = null; recommendationVersion++; }
          if (section) section.hidden = true;
          return;
        }
        if (!isOpen()) return;
        const currency = window.Shopify?.currency?.active || 'AUD';
        const lines = [...cart.querySelectorAll('.kaktusc-cart__products .kaktusc-cart__product')];
        const key = `${Math.floor(Date.now() / 60000)}:${currency}:${lines.map(item => [item.dataset.id, item.querySelector('.kaktusc-cart__product-title')?.textContent, item.querySelector('.kaktusc-cart__product-variant')?.textContent, item.querySelector('.kaktusc-cart__product-qnt')?.textContent].join(':')).join('|')}`;
        if (key === recommendationKey) {
          if (recommendationData) renderRecommendations(cart, recommendationData);
          return;
        }
        recommendationKey = key;
        recommendationData = null;
        if (section) section.hidden = true;
        const version = ++recommendationVersion;
        const root = window.Shopify?.routes?.root || '/';
        // One cart read per changed line signature. The recommendation cache survives app DOM replacement.
        fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } })
          .then(response => response.ok ? response.json() : Promise.reject(new Error('Cart unavailable')))
          .then(async current => {
            const ids = new Set((current.items || []).filter(item => item.quantity > 0).map(item => String(item.product_id)));
            const seed = [...ids].find(id => /^\d+$/.test(id));
            const cartCurrency = /^[A-Z]{3}$/.test(current.currency) ? current.currency : currency;
            if (!seed) return { products: [], currency: cartCurrency };
            const products = await relatedProducts(seed, cartCurrency);
            const remaining = source.dataset.country === 'AU' && cartCurrency === 'AUD' && current.total_price >= 7000 && current.total_price < 10000 ? 10000 - current.total_price : 0;
            products.forEach(product => {
              const variant = (product.variants || []).filter(v => v.available === true && Number.isInteger(v.price)).sort((a,b) => a.price - b.price)[0];
              product.dutVariantId = variant?.id;
              product.price = variant?.price;
              product.price_varies = false;
            });
            if (remaining) products.sort((a,b) => {
              // Prefer the smallest available add-on that crosses the threshold, then those nearest it.
              const score = product => product.price >= remaining ? product.price - remaining : 10000 + remaining - product.price;
              return score(a) - score(b);
            });
            const unique = new Set();
            return { remaining, currency: cartCurrency, products: products.filter(product => {
              const id = String(product.id);
              const image = typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.url || product.featured_image?.src;
              if (!product.dutVariantId || (remaining && product.price > 4000) || !/^\d+$/.test(id) || ids.has(id) || unique.has(id) || product.available !== true || !Number.isInteger(product.price) || product.price < 0 || !product.handle || !product.title || !image) return false;
              try { if (!['http:', 'https:'].includes(new URL(image, location.origin).protocol)) return false; } catch (_) { return false; }
              unique.add(id);
              product.image = image;
              return true;
            }).slice(0, 2) };
          })
          .catch(() => ({ products: [], currency }))
          .then(data => {
            if (version !== recommendationVersion) return;
            recommendationData = data;
            refresh();
          });
      };

      const refresh = () => {
        const cart = panel();
        if (!cart) return;
        if (!readyAnnounced && document.getElementById('kaktusc-cart-icon')) {
          readyAnnounced = true;
          window.dispatchEvent(new Event('dut:cart-ready'));
        }
        // Read actual line items, never recommendations or a saved countdown value.
        const hasItems = [...cart.querySelectorAll('.kaktusc-cart__products .kaktusc-cart__product')]
          .some(item => Number(item.querySelector('.kaktusc-cart__product-qnt')?.textContent.trim()) > 0);
        doc.documentElement.dataset.dutCartHasItems = String(hasItems);
        cart.setAttribute('role', 'dialog');
        cart.setAttribute('aria-modal', 'true');
        cart.setAttribute('aria-label', 'Your shopping bag');
        cart.tabIndex = -1;
        const heading = cart.querySelector('.kaktusc-cart__top h2');
        if (brandIcon && heading && !heading.querySelector('.dut-cart-brand')) {
          const icon = doc.createElement('img');
          icon.className = 'dut-cart-brand';
          icon.src = brandIcon;
          icon.alt = '';
          icon.width = 32;
          icon.height = 32;
          heading.prepend(icon);
        }
        button(doc.querySelector('.kaktusc-cart__close'), 'Close shopping bag');
        doc.querySelectorAll('.kaktusc-cart__product').forEach((item) => {
          const name = item.querySelector('.kaktusc-cart__product-title')?.textContent.trim() || 'item';
          button(item.querySelector('.kaktusc-cart__product-qnt--minus'), `Decrease quantity of ${name}`);
          button(item.querySelector('.kaktusc-cart__product-qnt--plus'), `Increase quantity of ${name}`);
          button(item.querySelector('.kaktusc-cart__product-delete'), `Remove ${name}`);
          const quantity = item.querySelector('.kaktusc-cart__product-qnt');
          quantity?.setAttribute('aria-live', 'polite');
        });
        doc.querySelectorAll('.kaktusc-cart__offer-product').forEach((item) => {
          const name = item.querySelector('.kaktusc-cart__offer-product-title')?.textContent.trim() || 'recommended item';
          button(item.querySelector('.kaktusc-cart__offer-product-btn'), `Add ${name}`);
          if (item.dataset.offerid !== '9665' || !/^\d+$/.test(item.dataset.id || '')) return;
          const currency = window.Shopify?.currency?.active || 'AUD';
          const entry = currentVariant(item.dataset.id, currency);
          const available = entry.status === 'ready' && entry.variant.available;
          item.hidden = !available;
          if (entry.status === 'pending') {
            if (pendingOffers.get(item) !== entry) {
              pendingOffers.set(item, entry);
              entry.promise.then(() => { if (item.isConnected) refresh(); });
            }
            return;
          }
          // A future app-configured discount gets its own pricing display unchanged.
          if (!available || item.querySelector('.kaktusc-cart__offer-product-discount')) return;
          const price = item.querySelector('.kaktusc-cart__of-price-discounted');
          const quantity = Math.max(1, Number(item.dataset.qnt) || 1);
          const formatted = new Intl.NumberFormat('en-AU', { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format(entry.variant.price * quantity / 100);
          if (price && price.textContent !== formatted) price.textContent = formatted;
        });
        doc.querySelectorAll('.kaktusc-cart__offer-products-wrap').forEach((wrapper) => {
          const offers = [...wrapper.querySelectorAll('.kaktusc-cart__offer-product')];
          wrapper.hidden = !offers.some((offer) => !offer.hidden);
        });
        syncCartCount(cart);
        updateRecommendations(cart, hasItems);
        doc.querySelector('.kaktusc-cart__discount-code-in')?.setAttribute('aria-label', 'Discount code');
        doc.querySelector('.kaktusc-cart__notes')?.setAttribute('aria-label', 'Order note');
        doc.querySelector('.kaktusc-cart__total-price')?.setAttribute('aria-live', 'polite');
        const summary = cart.querySelector('.kaktusc-cart__summary');
        if (summary && !summary.querySelector('.dut-cart-assurance')) {
          const assurance = doc.createElement('div');
          assurance.className = 'dut-cart-assurance';
          assurance.innerHTML = '<span><svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m3.5 7 8.5-4.5L20.5 7v10L12 21.5 3.5 17Z M3.5 7 12 12l8.5-5M12 12v9.5M8 4.7l8.5 4.8"/></svg>Plain packaging</span><span><svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><rect x="5" y="10" width="14" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v3"/></svg>Secure checkout</span>';
          summary.prepend(assurance);
        }


        const open = isOpen();
        if (open && focusedControl && !focusedControl.isConnected && doc.activeElement === doc.body) {
          const label = focusedControl.getAttribute('aria-label');
          const replacement = [...cart.querySelectorAll('[aria-label]')].find((element) => element.getAttribute('aria-label') === label);
          (replacement || doc.querySelector('.kaktusc-cart__close'))?.focus({ preventScroll: true });
        }
        if (open && !wasOpen) {
          returnFocus = requestedOpener || (document.activeElement !== frame ? document.activeElement : document.querySelector('#cart-icon-bubble'));
          requestedOpener = null;
          window.dispatchEvent(new Event('dut:cart-open'));
          if (!focusPending) {
            focusPending = true;
            requestAnimationFrame(() => {
              focusPending = false;
              if (isOpen()) doc.querySelector('.kaktusc-cart__close')?.focus({ preventScroll: true });
            });
          }
        } else if (!open && wasOpen) {
          if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
        }
        wasOpen = open;
      };

      doc.addEventListener('focusin', (event) => { focusedControl = event.target; });
      doc.addEventListener('keydown', (event) => {
        if (!isOpen()) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          close();
        } else if (event.key === 'Tab') {
          const items = focusables();
          const first = items[0];
          const last = items[items.length - 1];
          if (!first) { event.preventDefault(); panel()?.focus(); }
          else if (event.shiftKey && (doc.activeElement === first || !panel().contains(doc.activeElement))) {
            event.preventDefault(); last.focus();
          } else if (!event.shiftKey && (doc.activeElement === last || !panel().contains(doc.activeElement))) {
            event.preventDefault(); first.focus();
          }
        } else if ((event.key === 'Enter' || event.key === ' ') && event.target.dataset.dutButton) {
          event.preventDefault(); event.target.click();
        }
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && isOpen()) { event.preventDefault(); close(); }
      });
      new MutationObserver(refresh).observe(doc.body, { childList: true, subtree: true, characterData: true });
      if (host) new MutationObserver(refresh).observe(host, { attributes: true, attributeFilter: ['style', 'class'] });
      refresh();
    };

    frame.addEventListener('load', enhance);
    enhance();
  };
  const discover = () => document.querySelectorAll('#kaktusc-widget').forEach(attachFrame);
  new MutationObserver(discover).observe(document.body, { childList: true, subtree: true });
  discover();
})();
